import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { throwError } from 'rxjs';
import { EntityContextService } from '../services/entity-context.service';
import { ACCOUNTING_API_BASE } from '../../accounting-module.config';

// Adds the X-Entity-Id header to every accounting request. The Authorization
// bearer header is handled by MSAL's own interceptor (MsalInterceptor) — this
// only attaches the active entity id, which is plain state (no JWT).
//
// Scoping note: in the standalone BC_Accounting_UI this matched on the substring
// '/accounting/', which was safe because every request the app made was an accounting
// request. Inside BC_UI this interceptor sees ALL traffic — Corporate, Identity, Invoicing,
// Tax — so a loose substring match would throw the "no active entity" error at any unrelated
// BC_UI URL that happened to contain '/accounting/'. Match the resolved API base instead.
const ENTITY_FREE = ['/companies', '/dev-login', '/dev/'];

// `/entities` is the discovery call and `/entities/switch` picks one, so neither can require an
// entity — but they are the only two. `/entities/{id}` is entity-scoped work, and the API derives
// the caller's role claim (owner|admin|accountant) solely from X-Entity-Id: matching the whole
// `/entities` prefix stripped the header off every entity write and the API answered 403.
const ENTITY_FREE_EXACT = ['/entities', '/entities/switch'];

export const entityHeaderInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(ACCOUNTING_API_BASE)) return next(req);

  // Endpoints that work without an active entity (the entity discovery/selection flow).
  const path = req.url.slice(ACCOUNTING_API_BASE.length);
  const route = path.split('?')[0].replace(/[/]$/, '');
  if (ENTITY_FREE_EXACT.includes(route) || ENTITY_FREE.some(p => path.startsWith(p))) return next(req);

  const ctx = inject(EntityContextService);
  const id = ctx.activeEntity()?.id;
  if (!id) return throwError(() => new HttpErrorResponse({
    status: 0,
    statusText: 'No active entity — select an entity before making accounting requests',
    url: req.url,
  }));

  return next(req.clone({ setHeaders: { 'X-Entity-Id': id } }));
};

// Kept for backwards compat with the old import path.
export { entityHeaderInterceptor as entityTokenInterceptor };
